import { useCollapsedList, ShowAllButton } from '../infrastructure/widgets/DashboardKit';
import { WelcomeBanner } from '../infrastructure/widgets/WelcomeBanner';
// The owner's dashboard: the whole company on one screen, for any period.
// Every figure comes from GET /api/dashboard-kpi/owner (src/api-dashboard-kpi/
// owner.js) except stock, activity, visits and meetings, which reuse the same
// hooks their own pages use. Each money figure sits next to the previous
// period of the same length, so "is this good?" answers itself.

import { useMemo, useState } from 'react';
import type { ReactNode } from 'react';
import type { LucideIcon } from 'lucide-react';
import {
  TrendingUp, TrendingDown, RefreshCw, Receipt, Wallet, FileText, Landmark, Truck, Target,
  AlertTriangle, ChevronRight, CalendarDays, Activity, MapPin, Inbox, CheckSquare, Clock,
  FilePen, FlaskConical, Boxes, UserX, Droplets,
} from 'lucide-react';
import { Link, useNavigate } from 'react-router-dom';
import {
  ResponsiveContainer, ComposedChart, Bar, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend,
} from 'recharts';
import { useAlerts, useBalances } from '@/features/inventory/hooks/useInventoryApi';
import { ApprovalsQueueCard } from '@/features/approvals/components/ApprovalsQueueCard';
import { useVisitDashboard } from '@/features/visits/hooks/useVisits';
import { useMeetingCalendar } from '@/features/meetings/hooks/useMeetings';
import { MeetingCalendar } from '@/features/meetings/components/MeetingCalendar';
import type { Meeting } from '@/features/meetings/types';
import { useActivityFeed } from '@/features/activity/hooks/useActivity';
import { STAGE_LABELS } from '@/features/deals/constants';
import { ROUTES } from '@/routes/paths';
import { useOwnerDashboard, type PeriodFigure } from '../hooks/useOwnerDashboard';
import { useTeamAttendance } from '@/features/attendance/hooks/useAttendance';
import { formatClock, formatDuration, minutesOnDuty } from '@/features/attendance/utils';
import { humanizeEnum } from '@/shared/utils/utils';

// ── formatting ──────────────────────────────────────────────────────────────
const inr = (v: number) => {
  const n = Number(v) || 0;
  const abs = Math.abs(n);
  if (abs >= 1e7) return `₹${(n / 1e7).toFixed(2)} Cr`;
  if (abs >= 1e5) return `₹${(n / 1e5).toFixed(2)} L`;
  return `₹${Math.round(n).toLocaleString('en-IN')}`;
};
const inrAxis = (v: number) => (v >= 1e7 ? `${(v / 1e7).toFixed(1)}Cr` : v >= 1e5 ? `${(v / 1e5).toFixed(1)}L` : v >= 1e3 ? `${Math.round(v / 1e3)}k` : String(v));
const num = (v: number) => (Number(v) || 0).toLocaleString('en-IN');

function dayKey(d: Date) {
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}
const shortDate = (k: string) => new Date(`${k}T00:00:00`).toLocaleDateString('en-IN', { day: 'numeric', month: 'short' });
function timeAgo(iso?: string | null): string {
  if (!iso) return 'never';
  const mins = Math.floor((Date.now() - new Date(iso).getTime()) / 60000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  return `${Math.floor(hrs / 24)}d ago`;
}
const formatAction = (a: string) => a.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());

function gridBounds(month: Date): { from: string; to: string } {
  const first = new Date(month.getFullYear(), month.getMonth(), 1);
  const start = new Date(month.getFullYear(), month.getMonth(), 1 - first.getDay());
  const end = new Date(start.getFullYear(), start.getMonth(), start.getDate() + 41);
  return { from: dayKey(start), to: dayKey(end) };
}

// ── periods ─────────────────────────────────────────────────────────────────
type PresetKey = 'today' | 'yesterday' | 'week' | 'month' | 'last_month' | 'fy';
const PRESETS: { key: PresetKey; label: string }[] = [
  { key: 'today', label: 'Today' },
  { key: 'yesterday', label: 'Yesterday' },
  { key: 'week', label: 'This week' },
  { key: 'month', label: 'This month' },
  { key: 'last_month', label: 'Last month' },
  { key: 'fy', label: 'This FY' },
];
function presetRange(key: PresetKey, now = new Date()): { from: string; to: string } {
  const today = dayKey(now);
  const d = (y: number, m: number, day: number) => dayKey(new Date(y, m, day));
  const y = now.getFullYear();
  const m = now.getMonth();
  switch (key) {
    case 'today': return { from: today, to: today };
    case 'yesterday': { const t = d(y, m, now.getDate() - 1); return { from: t, to: t }; }
    case 'week': { const back = (now.getDay() + 6) % 7; return { from: d(y, m, now.getDate() - back), to: today }; }
    case 'last_month': return { from: d(y, m - 1, 1), to: d(y, m, 0) };
    case 'fy': return { from: d(m >= 3 ? y : y - 1, 3, 1), to: today };
    default: return { from: d(y, m, 1), to: today };
  }
}

// ── building blocks ─────────────────────────────────────────────────────────
const TONE = {
  indigo: 'bg-primary-container text-primary',
  emerald: 'bg-success/10 text-success-text',
  amber: 'bg-warning/10 text-warning-text',
  rose: 'bg-danger/5 text-danger-text',
  sky: 'bg-info/10 text-info-text',
  violet: 'bg-primary-container text-primary',
} as const;
type Tone = keyof typeof TONE;

function Change({ value, invert = false }: { value: number | null | undefined; invert?: boolean }) {
  if (value === null || value === undefined) return <span className="text-on-surface-subtle">no earlier data</span>;
  const good = invert ? value < 0 : value >= 0;
  const Icon = value >= 0 ? TrendingUp : TrendingDown;
  return (
    <span className={`inline-flex items-center gap-0.5 font-semibold ${good ? 'text-success-text' : 'text-danger-text'}`}>
      <Icon className="h-3 w-3" />{value > 0 ? '+' : ''}{value}%
    </span>
  );
}

function Kpi({ label, value, icon: Icon, tone, sub, footer, to }: {
  label: string; value: ReactNode; icon: LucideIcon; tone: Tone; sub?: ReactNode; footer?: ReactNode; to?: string;
}) {
  const body = (
    <div className={`h-full rounded-card border border-border/80 bg-card p-4 shadow-surface-1 transition ${to ? 'hover:border-primary/30 hover:shadow-surface-2' : ''}`}>
      <div className="flex items-start justify-between gap-2">
        <span className="text-data-xs font-semibold uppercase leading-snug tracking-wider text-on-surface-variant">{label}</span>
        <span className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-lg ${TONE[tone]}`}><Icon className="h-4 w-4" /></span>
      </div>
      <div className="mt-2 truncate font-mono text-[22px] font-semibold leading-tight text-on-surface tabular-nums">{value}</div>
      {sub && <div className="mt-0.5 truncate text-data-xs font-medium text-on-surface-variant">{sub}</div>}
      {footer && <div className="mt-2 border-t border-border pt-2 text-data-xs text-on-surface-variant">{footer}</div>}
    </div>
  );
  return to ? <Link to={to} className="block h-full">{body}</Link> : body;
}

function Card({ title, action, children, className = '' }: { title: string; action?: ReactNode; children: ReactNode; className?: string }) {
  return (
    <section className={`flex flex-col overflow-hidden rounded-card border border-border/80 bg-card shadow-surface-1 ${className}`}>
      <div className="flex items-center justify-between gap-2 border-b border-border bg-surface-container-low/60 px-5 py-3">
        <h3 className="text-body-md font-semibold text-on-surface">{title}</h3>
        {action}
      </div>
      <div className="flex-1 p-5">{children}</div>
    </section>
  );
}

function More({ to, label }: { to: string; label: string }) {
  return (
    <Link to={to} className="flex items-center gap-0.5 text-body-sm font-semibold text-primary hover:underline">
      {label}<ChevronRight className="h-3.5 w-3.5" />
    </Link>
  );
}

function Empty({ text }: { text: string }) {
  return <div className="py-6 text-center text-body-sm text-on-surface-subtle">{text}</div>;
}
function Unavailable() {
  return <div className="py-6 text-center text-body-sm text-warning-text">Could not load this section — try Refresh.</div>;
}

function Bars({ rows, color = 'bg-primary' }: { rows: { label: string; value: number; right: ReactNode }[]; color?: string }) {
  const max = Math.max(1, ...rows.map((r) => r.value));
  return (
    <div className="space-y-2">
      {rows.map((r) => (
        <div key={r.label} className="text-body-sm">
          <div className="flex items-baseline gap-3">
            <span className="min-w-0 flex-1 truncate font-semibold text-on-surface-variant" title={r.label}>{r.label}</span>
            <span className="shrink-0 whitespace-nowrap tabular-nums">{r.right}</span>
          </div>
          <div className="mt-1.5 h-1.5 overflow-hidden rounded-full bg-surface-container">
            <div className={`h-full rounded-full ${color}`} style={{ width: `${Math.max(3, Math.round((r.value / max) * 100))}%` }} />
          </div>
        </div>
      ))}
    </div>
  );
}

function periodSub(f: PeriodFigure | undefined, what: string) {
  if (!f) return undefined;
  return `${num(f.count)} ${what}${f.count === 1 ? '' : 's'}`;
}

const SOURCE_LABELS: Record<string, string> = { indiamart: 'IndiaMART', website: 'Website', justdial: 'JustDial', tradeindia: 'TradeIndia', other: 'Direct / other', referral: 'Referral' };

// ── page ────────────────────────────────────────────────────────────────────
export function SuperAdminDashboard() {
  const navigate = useNavigate();
  const [preset, setPreset] = useState<PresetKey | 'custom'>('month');
  const [range, setRange] = useState(() => presetRange('month'));
  const choose = (key: PresetKey) => { setPreset(key); setRange(presetRange(key)); };

  const { data, isFetching, refetch, isError } = useOwnerDashboard(range.from, range.to);
  const { data: stockAlerts } = useAlerts();
  const { data: balancesData } = useBalances({ limit: 5000 });
  const today = dayKey(new Date());
  const { data: visits } = useVisitDashboard(today, 'all');
  const { data: activity = [] } = useActivityFeed({ from: today, to: today, exclude: ['login'], limit: 25, offset: 0 });
  const [month, setMonth] = useState(() => new Date());
  const cal = useMemo(() => gridBounds(month), [month]);
  const { data: calendar } = useMeetingCalendar(cal.from, cal.to);
  // Who is in today (check-in / check-out from the top-bar Attendance button).
  const { data: attendance = [] } = useTeamAttendance(true);
  const attendanceRows = useMemo(() => [...attendance].sort((a, b) =>
    (a.check_in ? (a.check_out ? 1 : 0) : 2) - (b.check_in ? (b.check_out ? 1 : 0) : 2) || a.display_name.localeCompare(b.display_name)), [attendance]);
  const attList = useCollapsedList(attendanceRows, 8);
  const inNow = attendance.filter((a) => a.check_in && !a.check_out).length;
  const checkedOut = attendance.filter((a) => a.check_out).length;
  const notIn = attendance.filter((a) => !a.check_in).length;

  const stockValue = useMemo(() => (balancesData?.data ?? []).reduce((s, b) => s + (b.totalValue || 0), 0), [balancesData]);
  const lowStock = stockAlerts?.total ?? 0;

  const m = data?.money;
  const leads = data?.leads;
  const dispatch = data?.dispatch;
  const att = data?.attention;
  const periodLabel = data ? (data.period.from === data.period.to ? shortDate(data.period.from) : `${shortDate(data.period.from)} – ${shortDate(data.period.to)}`) : '';
  const prevLabel = data ? (data.period.previousFrom === data.period.previousTo ? shortDate(data.period.previousFrom) : `${shortDate(data.period.previousFrom)} – ${shortDate(data.period.previousTo)}`) : '';

  const attention: { label: string; count: number; icon: LucideIcon; to: string; tone: 'rose' | 'amber' | 'sky' }[] = att ? [
    { label: 'Edit approvals waiting', count: att.editApprovals, icon: CheckSquare, to: ROUTES.invoiceEditApprovals, tone: 'rose' },
    { label: 'Material requests to approve', count: att.materialApprovals ?? 0, icon: Boxes, to: `${ROUTES.inventory}?tab=requests`, tone: 'rose' },
    { label: 'Overdue dispatches', count: dispatch?.overdue.count ?? 0, icon: Truck, to: ROUTES.dispatchOrders, tone: 'rose' },
    { label: 'Overdue follow-ups', count: att.overdueFollowups, icon: Clock, to: ROUTES.deals, tone: 'rose' },
    { label: 'Enquiries not assigned', count: att.enquiries, icon: Inbox, to: ROUTES.leadInbox, tone: 'amber' },
    { label: 'Leave requests', count: att.leaves, icon: UserX, to: ROUTES.leaves, tone: 'amber' },
    { label: 'Draft invoices', count: att.draftInvoices, icon: FilePen, to: ROUTES.invoices, tone: 'amber' },
    { label: 'Quotations expiring (4 days)', count: att.quotationsExpiring, icon: FileText, to: ROUTES.quotations, tone: 'amber' },
    { label: 'Samples to dispatch', count: dispatch?.samplesToDispatch ?? 0, icon: FlaskConical, to: ROUTES.sampling, tone: 'sky' },
    { label: 'Samples awaiting feedback', count: att.samplesAwaitingFeedback, icon: FlaskConical, to: ROUTES.sampling, tone: 'sky' },
    { label: 'Products low on stock', count: lowStock, icon: Boxes, to: ROUTES.inventory, tone: 'sky' },
  ] : [];
  const attentionOpen = attention.filter((a) => a.count > 0);

  const chartData = (data?.trend?.points ?? []).map((p) => ({
    ...p,
    label: data?.trend?.granularity === 'month'
      ? new Date(`${p.key}-01T00:00:00`).toLocaleDateString('en-IN', { month: 'short', year: '2-digit' })
      : shortDate(p.key),
  }));
  const trendHasData = chartData.some((p) => p.invoiced || p.collected);

  return (
    <div className="mx-auto max-w-[1580px] space-y-4 pb-14 font-sans text-on-surface">
      {/* Header + period — the shared WelcomeBanner, period controls inside it. */}
      <WelcomeBanner
        subtitle={data ? <>Showing <b className="font-semibold text-on-surface">{periodLabel}</b>, compared with {prevLabel}</> : 'Company overview'}
      >
        <div className="flex flex-wrap items-center gap-2">
          <div className="flex rounded-lg border border-border bg-card p-0.5 shadow-xs">
            {PRESETS.map((p) => (
              <button key={p.key} type="button" onClick={() => choose(p.key)}
                className={`rounded-lg px-2.5 py-1.5 text-body-sm font-semibold transition ${preset === p.key ? 'bg-primary text-on-primary' : 'text-on-surface-variant hover:bg-surface-variant'}`}>
                {p.label}
              </button>
            ))}
          </div>
          <div className="flex items-center gap-1 rounded-lg border border-border bg-card px-2 py-1 shadow-xs">
            <CalendarDays className="h-3.5 w-3.5 text-on-surface-subtle" />
            <input type="date" value={range.from} max={range.to} onChange={(e) => { setPreset('custom'); setRange((r) => ({ ...r, from: e.target.value || r.from })); }} className="bg-transparent text-body-sm text-on-surface-variant outline-none" />
            <span className="text-body-sm text-on-surface-subtle">to</span>
            <input type="date" value={range.to} min={range.from} onChange={(e) => { setPreset('custom'); setRange((r) => ({ ...r, to: e.target.value || r.to })); }} className="bg-transparent text-body-sm text-on-surface-variant outline-none" />
          </div>
          <button type="button" onClick={() => refetch()}
            className="flex items-center gap-1.5 rounded-lg border border-border bg-card px-3 py-1.5 text-body-sm font-semibold text-on-surface-variant shadow-xs hover:bg-surface-variant">
            <RefreshCw className={`h-3.5 w-3.5 ${isFetching ? 'animate-spin' : ''}`} />Refresh
          </button>
        </div>
      </WelcomeBanner>

      {isError && (
        <div className="rounded-lg border border-warning/30 bg-warning/10 px-4 py-3 text-body-md text-warning-text">
          The dashboard figures could not be loaded. Check your connection and press Refresh.
        </div>
      )}

      {/* Money KPIs */}
      <div className="grid grid-cols-2 gap-3 md:grid-cols-3 xl:grid-cols-6">
        <Kpi label="Sales invoiced" icon={Receipt} tone="indigo" to={ROUTES.invoices}
          value={m ? inr(m.sales.value) : '—'} sub={periodSub(m?.sales, 'invoice')}
          footer={m && <><Change value={m.sales.change} /> <span>vs {inr(m.sales.previous)}</span></>} />
        <Kpi label="Collected" icon={Wallet} tone="emerald" to={ROUTES.accountingV2Vouchers}
          value={m ? inr(m.collected.value) : '—'} sub={periodSub(m?.collected, 'receipt')}
          footer={m && <><Change value={m.collected.change} /> <span>vs {inr(m.collected.previous)}</span></>} />
        <Kpi label="To collect (all)" icon={Landmark} tone="rose" to={ROUTES.invoices}
          value={m ? inr(m.receivables.total) : '—'} sub={m ? `${num(m.receivables.invoices)} unpaid invoices` : undefined}
          footer={m && (m.receivables.overdue > 0
            ? <span className="font-semibold text-danger-text">{inr(m.receivables.overdue)} overdue</span>
            : <span className="text-success-text">Nothing overdue</span>)} />
        <Kpi label="Quotations sent" icon={FileText} tone="amber" to={ROUTES.quotations}
          value={m ? inr(m.quotations.value) : '—'} sub={periodSub(m?.quotations, 'quotation')}
          footer={m && <><Change value={m.quotations.change} /> <span>vs {inr(m.quotations.previous)}</span></>} />
        <Kpi label="New leads" icon={Target} tone="violet" to={ROUTES.deals}
          value={leads ? num(leads.created) : '—'}
          sub={leads ? `${leads.conversionRate ?? 0}% converted · ${num(leads.lost)} lost` : undefined}
          footer={leads && <><Change value={leads.change} /> <span>vs {num(leads.createdPrevious)}</span></>} />
        <Kpi label="Awaiting dispatch" icon={Truck} tone="sky" to={ROUTES.dispatchOrders}
          value={dispatch ? inr(dispatch.awaiting.value) : '—'} sub={dispatch ? `${num(dispatch.awaiting.count)} orders` : undefined}
          footer={dispatch && (dispatch.overdue.count > 0
            ? <span className="font-semibold text-danger-text">{num(dispatch.overdue.count)} overdue · {inr(dispatch.overdue.value)}</span>
            : <span className="text-success-text">None overdue</span>)} />
      </div>

      {/* Needs attention */}
      <Card title="Needs your attention" action={<span className="text-data-xs font-semibold text-on-surface-variant">right now</span>}>
        {!att ? (data ? <Unavailable /> : <Empty text="Loading…" />) : attentionOpen.length === 0 ? (
          <div className="py-2 text-center text-body-md font-semibold text-success-text">All clear — nothing is waiting on anyone.</div>
        ) : (
          <div className="grid grid-cols-2 gap-2 md:grid-cols-3 xl:grid-cols-5">
            {attentionOpen.map((a) => (
              <button key={a.label} type="button" onClick={() => navigate(a.to)}
                className={`flex items-center gap-2.5 rounded-lg border p-2.5 text-left transition hover:shadow-xs ${a.tone === 'rose' ? 'border-danger/30 bg-danger/5' : a.tone === 'amber' ? 'border-warning/30 bg-warning/10' : 'border-info/30 bg-info/10'}`}>
                <a.icon className={`h-4 w-4 shrink-0 ${a.tone === 'rose' ? 'text-danger-text' : a.tone === 'amber' ? 'text-warning-text' : 'text-info-text'}`} />
                <span className="text-data-lg font-semibold tabular-nums text-on-surface">{num(a.count)}</span>
                <span className="text-data-xs font-semibold leading-tight text-on-surface-variant">{a.label}</span>
              </button>
            ))}
          </div>
        )}
      </Card>

      {/* Approvals — invoice edits after final, Dispatch's material requests */}
      <ApprovalsQueueCard />

      {/* Trend + receivables */}
      <div className="grid grid-cols-1 gap-4 xl:grid-cols-12">
        <Card title={`Sales vs collections${data?.trend?.granularity === 'month' ? ' (by month)' : ''}`} className="xl:col-span-8"
          action={m && <span className="text-data-xs font-semibold text-on-surface-variant">Invoiced {inr(m.sales.value)} · Collected {inr(m.collected.value)}</span>}>
          {data && !data.trend ? <Unavailable /> : !trendHasData ? <Empty text="No invoices or receipts in this period" /> : (
            <div className="h-[340px]">
              <ResponsiveContainer width="100%" height="100%">
                <ComposedChart data={chartData} margin={{ top: 5, right: 8, left: 0, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#eef2f7" vertical={false} />
                  <XAxis dataKey="label" tick={{ fontSize: 10, fill: '#64748b' }} interval="preserveStartEnd" />
                  <YAxis tickFormatter={inrAxis} tick={{ fontSize: 10, fill: '#64748b' }} width={48} />
                  <Tooltip formatter={(v: number) => inr(v)} contentStyle={{ fontSize: 12, borderRadius: 10 }} />
                  <Legend wrapperStyle={{ fontSize: 11 }} />
                  <Bar dataKey="invoiced" name="Invoiced" fill="#6366f1" radius={[4, 4, 0, 0]} maxBarSize={28} />
                  <Line dataKey="collected" name="Collected" stroke="#12b76a" strokeWidth={2} dot={false} type="monotone" />
                </ComposedChart>
              </ResponsiveContainer>
            </div>
          )}
        </Card>

        <Card title="Money to collect" className="xl:col-span-4" action={<More to={ROUTES.invoices} label="Invoices" />}>
          {!m ? (data ? <Unavailable /> : <Empty text="Loading…" />) : (
            <>
              <div className="mb-3 grid grid-cols-5 gap-1">
                {m.receivables.aging.map((a) => (
                  <div key={a.key} className={`rounded-lg border p-1.5 text-center ${a.key !== 'not_due' && a.value > 0 ? 'border-danger/30 bg-danger/5' : 'border-border bg-surface-variant'}`}>
                    <div className={`text-data-xs font-semibold tabular-nums ${a.key !== 'not_due' && a.value > 0 ? 'text-danger-text' : 'text-on-surface'}`}>{inr(a.value)}</div>
                    <div className="text-data-xs font-semibold uppercase leading-tight text-on-surface-variant">{a.label}</div>
                  </div>
                ))}
              </div>
              <div className="mb-1.5 text-data-xs font-semibold uppercase text-on-surface-subtle">Who owes the most</div>
              {m.receivables.topDebtors.length === 0 ? <Empty text="Every invoice is paid" /> : (
                <div className="space-y-1.5">
                  {m.receivables.topDebtors.slice(0, 6).map((d) => (
                    <div key={d.customerId || d.name} className="flex items-center justify-between rounded-lg border border-border bg-surface-variant/60 px-2.5 py-1.5 text-body-sm">
                      <div className="min-w-0">
                        <div className="truncate font-semibold text-on-surface">{d.name}</div>
                        <div className="text-data-xs text-on-surface-variant">{d.invoices} invoice{d.invoices === 1 ? '' : 's'}{d.oldestDays > 0 ? ` · oldest ${d.oldestDays} days late` : ' · not yet due'}</div>
                      </div>
                      <div className="ml-2 shrink-0 text-right">
                        <div className="font-semibold tabular-nums text-on-surface">{inr(d.due)}</div>
                        {d.overdue > 0 && <div className="text-data-xs font-semibold text-danger-text">{inr(d.overdue)} overdue</div>}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </>
          )}
        </Card>
      </div>

      {/* Team scoreboard */}
      <Card title="Sales team scoreboard" action={<span className="text-data-xs font-semibold text-on-surface-variant">{periodLabel} · open leads and follow-ups are as of now</span>}>
        {!data?.team ? (data ? <Unavailable /> : <Empty text="Loading…" />) : data.team.length === 0 ? <Empty text="No active salespeople" /> : (
          <div className="overflow-x-auto">
            <table className="w-full text-body-sm">
              <thead>
                <tr className="border-b border-border text-left text-data-xs font-semibold uppercase text-on-surface-subtle">
                  <th className="py-2 pr-2">Salesperson</th>
                  <th className="px-2 py-2 text-right">New leads</th>
                  <th className="px-2 py-2 text-right">Open leads</th>
                  <th className="px-2 py-2 text-right">Quotations</th>
                  <th className="px-2 py-2 text-right">Invoiced</th>
                  <th className="px-2 py-2 text-right">Collected</th>
                  <th className="px-2 py-2 text-right">Month target</th>
                  <th className="px-2 py-2 text-right">Overdue follow-ups</th>
                  <th className="py-2 pl-2 text-right">Last active</th>
                </tr>
              </thead>
              <tbody>
                {data.team.map((t) => {
                  const pct = t.monthTarget > 0 ? Math.round((t.sales / t.monthTarget) * 100) : null;
                  const stale = !t.lastSeenAt || Date.now() - new Date(t.lastSeenAt).getTime() > 2 * 86400000;
                  return (
                    <tr key={t.userId} className="border-b border-border hover:bg-surface-variant/60">
                      <td className="py-2 pr-2 font-semibold text-on-surface">{t.name}{t.role === 'admin' && <span className="ml-1.5 rounded bg-primary-container px-1 text-data-xs font-semibold uppercase text-primary">Head</span>}</td>
                      <td className="font-mono px-2 py-2 text-right font-semibold tabular-nums text-data-sm">{num(t.leadsNew)}</td>
                      <td className="font-mono px-2 py-2 text-right tabular-nums text-data-sm">{num(t.leadsOpen)} <span className="text-on-surface-subtle">· {inr(t.pipelineValue)}</span></td>
                      <td className="font-mono px-2 py-2 text-right tabular-nums text-data-sm">{num(t.quotations)} <span className="text-on-surface-subtle">· {inr(t.quotationValue)}</span></td>
                      <td className="font-mono px-2 py-2 text-right font-semibold tabular-nums text-primary-dim text-data-sm">{inr(t.sales)}</td>
                      <td className="font-mono px-2 py-2 text-right font-semibold tabular-nums text-success-text text-data-sm">{inr(t.collected)}</td>
                      <td className="font-mono px-2 py-2 text-right tabular-nums text-data-sm">{pct === null ? <span className="text-on-surface-subtle">not set</span> : <span className={pct >= 100 ? 'font-semibold text-success-text' : 'font-semibold text-warning-text'}>{pct}% of {inr(t.monthTarget)}</span>}</td>
                      <td className={`px-2 py-2 text-right font-semibold tabular-nums ${t.overdueFollowups > 0 ? 'text-danger-text' : 'text-on-surface-subtle'}`}>{num(t.overdueFollowups)}</td>
                      <td className={`py-2 pl-2 text-right ${stale ? 'font-semibold text-warning-text' : 'text-on-surface-variant'}`}>{timeAgo(t.lastSeenAt)}</td>
                    </tr>
                  );
                })}
              </tbody>
              <tfoot>
                {m && (() => {
                  const restSales = m.sales.value - data.team.reduce((s, t) => s + t.sales, 0);
                  const restCollected = m.collected.value - data.team.reduce((s, t) => s + t.collected, 0);
                  if (restSales < 1 && restCollected < 1) return null;
                  return (
                    <tr className="border-b border-border text-on-surface-variant">
                      <td className="py-2 pr-2 italic" colSpan={4}>Not linked to a salesperson (raised by Admin / Accounts)</td>
                      <td className="font-mono px-2 py-2 text-right font-semibold tabular-nums text-data-sm">{inr(Math.max(0, restSales))}</td>
                      <td className="font-mono px-2 py-2 text-right font-semibold tabular-nums text-data-sm">{inr(Math.max(0, restCollected))}</td>
                      <td colSpan={3} />
                    </tr>
                  );
                })()}
                <tr className="text-data-xs font-semibold text-on-surface-variant">
                  <td className="pr-2 pt-2">Total</td>
                  <td className="font-mono px-2 pt-2 text-right tabular-nums text-data-sm">{num(data.team.reduce((s, t) => s + t.leadsNew, 0))}</td>
                  <td className="font-mono px-2 pt-2 text-right tabular-nums text-data-sm">{num(data.team.reduce((s, t) => s + t.leadsOpen, 0))}</td>
                  <td className="font-mono px-2 pt-2 text-right tabular-nums text-data-sm">{num(data.team.reduce((s, t) => s + t.quotations, 0))}</td>
                  <td className="font-mono px-2 pt-2 text-right tabular-nums text-data-sm">{inr(m ? Math.max(m.sales.value, data.team.reduce((s, t) => s + t.sales, 0)) : data.team.reduce((s, t) => s + t.sales, 0))}</td>
                  <td className="font-mono px-2 pt-2 text-right tabular-nums text-data-sm">{inr(m ? Math.max(m.collected.value, data.team.reduce((s, t) => s + t.collected, 0)) : data.team.reduce((s, t) => s + t.collected, 0))}</td>
                  <td className="px-2 pt-2" />
                  <td className="font-mono px-2 pt-2 text-right tabular-nums text-data-sm">{num(data.team.reduce((s, t) => s + t.overdueFollowups, 0))}</td>
                  <td className="pl-2 pt-2" />
                </tr>
              </tfoot>
            </table>
          </div>
        )}
      </Card>

      {/* Attendance today */}
      <Card title="Attendance today" action={<More to={ROUTES.attendance} label="Attendance" />}>
        <div className="mb-3 flex flex-wrap gap-2 text-body-sm">
          <span className="rounded-full bg-success/10 px-3 py-1 font-semibold text-success-text">{inNow} in now</span>
          <span className="rounded-full bg-info/10 px-3 py-1 font-semibold text-info-text">{checkedOut} checked out</span>
          <span className={`rounded-full px-3 py-1 font-semibold ${notIn ? 'bg-warning/10 text-warning-text' : 'bg-surface-variant text-on-surface-variant'}`}>{notIn} not checked in</span>
        </div>
        {attendanceRows.length === 0 ? <Empty text="No employees to show" /> : (
          <div className="overflow-x-auto">
            <table className="w-full text-body-sm">
              <thead>
                <tr className="border-b border-border text-left text-data-xs font-semibold uppercase text-on-surface-subtle">
                  <th className="py-2 pr-2">Employee</th><th className="px-2 py-2">Department</th>
                  <th className="px-2 py-2">In</th><th className="px-2 py-2">Out</th><th className="px-2 py-2 text-right">Worked</th><th className="py-2 pl-2 text-right">Status</th>
                </tr>
              </thead>
              <tbody>
                {attList.visible.map((a) => (
                  <tr key={a.id} className="border-b border-border">
                    <td className="py-2 pr-2 font-semibold text-on-surface">{a.display_name}</td>
                    <td className="px-2 py-2 text-on-surface-variant">{humanizeEnum(a.department)}</td>
                    <td className="px-2 py-2 tabular-nums">{a.check_in ? formatClock(a.check_in) : '—'}</td>
                    <td className="px-2 py-2 tabular-nums">{a.check_out ? formatClock(a.check_out) : '—'}</td>
                    <td className="font-mono px-2 py-2 text-right tabular-nums text-data-sm">{a.check_in ? formatDuration(minutesOnDuty(a.check_in, a.check_out)) : '—'}</td>
                    <td className="font-mono py-2 pl-2 text-right tabular-nums text-data-sm">
                      {!a.check_in ? <span className="font-semibold text-warning-text">Not in</span>
                        : a.check_out ? <span className="font-semibold text-info-text">Checked out</span>
                        : <span className="font-semibold text-success-text">In</span>}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            <ShowAllButton hidden={attList.hidden} all={attList.all} onToggle={attList.toggle} />
          </div>
        )}
      </Card>

      {/* Leads + customers + products */}
      <div className="grid grid-cols-1 gap-4 xl:grid-cols-12">
        <Card title="Leads in this period" className="xl:col-span-5" action={<More to={ROUTES.deals} label="Pipeline" />}>
          {!leads ? (data ? <Unavailable /> : <Empty text="Loading…" />) : (
            <div className="space-y-4">
              <div className="grid grid-cols-4 gap-2 text-center">
                {[
                  { l: 'New', v: num(leads.created), c: 'text-on-surface' },
                  { l: 'Converted', v: num(leads.converted), c: 'text-success-text' },
                  { l: 'Lost', v: num(leads.lost), c: 'text-danger-text' },
                  { l: 'Conversion', v: `${leads.conversionRate ?? 0}%`, c: 'text-primary' },
                ].map((x) => (
                  <div key={x.l} className="rounded-lg border border-border bg-surface-variant/70 p-2">
                    <div className={`text-base font-semibold ${x.c}`}>{x.v}</div>
                    <div className="text-data-xs font-semibold uppercase text-on-surface-subtle">{x.l}</div>
                  </div>
                ))}
              </div>
              <div>
                <div className="mb-1.5 text-data-xs font-semibold uppercase text-on-surface-subtle">Where those leads stand now</div>
                {leads.byStage.length === 0 ? <Empty text="No new leads in this period" /> : (
                  <Bars rows={[...leads.byStage].sort((a, b) => b.count - a.count).map((s) => ({
                    label: STAGE_LABELS[s.stage] || s.stage, value: s.count,
                    right: <><b>{s.count}</b>{s.value > 0 && <span className="text-on-surface-subtle"> · {inr(s.value)}</span>}</>,
                  }))} />
                )}
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <div className="mb-1.5 text-data-xs font-semibold uppercase text-on-surface-subtle">Came from</div>
                  {leads.bySource.length === 0 ? <div className="text-body-sm text-on-surface-subtle">—</div> : leads.bySource.map((s) => (
                    <div key={s.source} className="flex justify-between py-0.5 text-body-sm"><span className="text-on-surface-variant">{SOURCE_LABELS[s.source] || s.source}</span><b>{s.count}</b></div>
                  ))}
                  <div className="mt-1 flex justify-between border-t border-border pt-1 text-body-sm"><span className="text-on-surface-variant">Enquiries received</span><b>{leads.enquiries.received}</b></div>
                </div>
                <div>
                  <div className="mb-1.5 text-data-xs font-semibold uppercase text-on-surface-subtle">Why we lost</div>
                  {leads.lostReasons.length === 0 ? <div className="text-body-sm text-on-surface-subtle">None lost</div> : leads.lostReasons.map((r) => (
                    <div key={r.reason} className="flex justify-between py-0.5 text-body-sm"><span className="truncate text-on-surface-variant">{r.reason}</span><b className="text-danger-text">{r.count}</b></div>
                  ))}
                </div>
              </div>
              <div className="rounded-lg bg-primary-container px-3 py-2 text-body-sm text-primary-dim">
                Open pipeline right now: <b>{num(leads.openPipeline.count)}</b> leads worth <b>{inr(leads.openPipeline.value)}</b>
              </div>
            </div>
          )}
        </Card>

        <Card title="Top customers" className="xl:col-span-3">
          {!data?.customers ? (data ? <Unavailable /> : <Empty text="Loading…" />) : data.customers.topCustomers.length === 0 ? <Empty text="No sales in this period" /> : (
            <Bars color="bg-success" rows={data.customers.topCustomers.map((c) => ({ label: c.name, value: c.value, right: <b>{inr(c.value)}</b> }))} />
          )}
        </Card>

        <Card title="Top products" className="xl:col-span-4"
          action={data?.customers && <span className="flex items-center gap-1 text-data-xs font-semibold text-info-text"><Droplets className="h-3.5 w-3.5" />{num(data.customers.litresSold)} litres sold</span>}>
          {!data?.customers ? (data ? <Unavailable /> : <Empty text="Loading…" />) : data.customers.topProducts.length === 0 ? <Empty text="No sales in this period" /> : (
            <table className="w-full text-body-sm">
              <thead><tr className="text-left text-data-xs font-semibold uppercase text-on-surface-subtle"><th className="pb-1.5">Product</th><th className="pb-1.5 text-right">Qty</th><th className="pb-1.5 text-right">Litres</th><th className="pb-1.5 text-right">Value</th></tr></thead>
              <tbody className="divide-y divide-slate-50">
                {data.customers.topProducts.map((p) => (
                  <tr key={p.name}>
                    <td className="max-w-[170px] truncate py-1.5 pr-2 font-semibold text-on-surface" title={p.name}>{p.name}</td>
                    <td className="font-mono py-1.5 text-right tabular-nums text-data-sm">{num(p.quantity)}</td>
                    <td className="font-mono py-1.5 text-right tabular-nums text-info-text text-data-sm">{p.litres ? num(p.litres) : '—'}</td>
                    <td className="font-mono py-1.5 text-right font-semibold tabular-nums text-data-sm">{inr(p.value)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </Card>
      </div>

      {/* Operations */}
      <div className="grid grid-cols-1 gap-4 xl:grid-cols-12">
        <Card title="Dispatch" className="xl:col-span-4" action={<More to={ROUTES.dispatchOrders} label="Open Dispatch" />}>
          {!dispatch ? (data ? <Unavailable /> : <Empty text="Loading…" />) : (
            <div className="space-y-2 text-body-sm">
              {[
                { l: 'Awaiting dispatch', v: dispatch.awaiting, c: 'text-on-surface' },
                { l: 'Overdue', v: dispatch.overdue, c: dispatch.overdue.count ? 'text-danger-text' : 'text-on-surface' },
                { l: `Dispatched (${periodLabel})`, v: dispatch.dispatched, c: 'text-success-text' },
              ].map((r) => (
                <div key={r.l} className="flex items-center justify-between rounded-lg border border-border bg-surface-variant/60 px-3 py-2">
                  <span className="font-semibold text-on-surface-variant">{r.l}</span>
                  <span className={`font-semibold tabular-nums ${r.c}`}>{num(r.v.count)} <span className="font-semibold text-on-surface-subtle">· {inr(r.v.value)}</span></span>
                </div>
              ))}
              <div className="flex items-center justify-between rounded-lg border border-border bg-surface-variant/60 px-3 py-2">
                <span className="font-semibold text-on-surface-variant">Samples to send</span>
                <span className="font-semibold tabular-nums">{num(dispatch.samplesToDispatch)}</span>
              </div>
            </div>
          )}
        </Card>

        <Card title="Stock" className="xl:col-span-4" action={<More to={ROUTES.inventory} label="Open Inventory" />}>
          <div className="mb-3 grid grid-cols-2 gap-2">
            <div className="rounded-lg border border-border bg-surface-variant/70 p-2.5 text-center"><div className="text-base font-semibold text-primary">{inr(stockValue)}</div><div className="text-data-xs font-semibold uppercase text-on-surface-subtle">Stock value</div></div>
            <div className="rounded-lg border border-border bg-surface-variant/70 p-2.5 text-center"><div className={`text-base font-semibold ${lowStock ? 'text-danger-text' : 'text-success-text'}`}>{num(lowStock)}</div><div className="text-data-xs font-semibold uppercase text-on-surface-subtle">Low on stock</div></div>
          </div>
          <div className="max-h-[170px] space-y-1.5 overflow-y-auto pr-1">
            {(stockAlerts?.alerts ?? []).slice(0, 6).map((a) => (
              <div key={a.productId} className="flex items-center justify-between rounded-lg border border-danger/30 bg-danger/5 px-2.5 py-1.5 text-body-sm">
                <span className="max-w-[180px] truncate font-semibold text-on-surface">{a.name}</span>
                <span className="ml-2 shrink-0 font-semibold text-danger-text"><AlertTriangle className="mr-1 inline h-3 w-3" />{a.currentStock} / {a.alertQuantity}</span>
              </div>
            ))}
            {lowStock === 0 && <Empty text="Nothing below its alert level" />}
          </div>
        </Card>

        <Card title="Field visits today" className="xl:col-span-4" action={<More to={ROUTES.visits} label="Visits" />}>
          <div className="mb-3 grid grid-cols-3 gap-2 text-center">
            {[{ l: 'Planned', v: visits?.planned ?? 0, c: 'text-warning-text' }, { l: 'Checked in', v: visits?.checkedIn ?? 0, c: 'text-info-text' }, { l: 'Completed', v: visits?.completed ?? 0, c: 'text-success-text' }].map((x) => (
              <div key={x.l} className="rounded-lg border border-border bg-surface-variant/70 p-2"><div className={`text-base font-semibold ${x.c}`}>{x.v}</div><div className="text-data-xs font-semibold uppercase text-on-surface-subtle">{x.l}</div></div>
            ))}
          </div>
          <div className="max-h-[150px] space-y-1.5 overflow-y-auto pr-1">
            {(visits?.repCounts ?? []).filter((r) => r.planned || r.checkedIn || r.completed).length === 0 ? <Empty text="No field visits planned today" /> :
              (visits?.repCounts ?? []).filter((r) => r.planned || r.checkedIn || r.completed).map((r) => (
                <div key={r.userId} className="flex items-center justify-between rounded-lg border border-border bg-surface-variant/60 px-2.5 py-1.5 text-body-sm">
                  <span className="flex items-center gap-1.5 font-semibold text-on-surface"><MapPin className="h-3 w-3 text-on-surface-subtle" />{r.displayName}</span>
                  <span className="font-semibold text-on-surface-variant">{r.completed} done / {r.planned} planned</span>
                </div>
              ))}
          </div>
        </Card>
      </div>

      {/* Activity + calendar */}
      <div className="grid grid-cols-1 gap-4 xl:grid-cols-12">
        <Card title="What happened today" className="xl:col-span-5" action={<span className="text-data-xs font-semibold text-on-surface-variant">{activity.length} events</span>}>
          <div className="max-h-[420px] space-y-1.5 overflow-y-auto pr-1">
            {activity.length === 0 ? <Empty text="Nothing logged yet today" /> : activity.map((a) => (
              <div key={a.id} className="flex items-start gap-2 rounded-lg p-1.5 text-body-sm hover:bg-surface-variant/70">
                <Activity className="mt-0.5 h-3 w-3 shrink-0 text-primary" />
                <div className="min-w-0 flex-1">
                  <div className="text-on-surface"><span className="font-semibold">{a.actor || 'System'}</span> {a.detail || formatAction(a.action)}</div>
                  <div className="text-data-xs text-on-surface-subtle">{timeAgo(a.created_at)}</div>
                </div>
              </div>
            ))}
          </div>
        </Card>
        <Card title="Meetings" className="xl:col-span-7" action={<More to={ROUTES.meetings} label="Open Meetings" />}>
          <MeetingCalendar
            month={month}
            meetings={calendar?.meetings ?? []}
            ceoBusy={calendar?.ceoBusy ?? []}
            onMonthChange={setMonth}
            onSelectDate={() => navigate(ROUTES.meetings)}
            onOpenMeeting={(mt: Meeting) => navigate(`${ROUTES.meetings}?meetingId=${mt.id}`)}
          />
        </Card>
      </div>
    </div>
  );
}
